<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import { useQuasar, QField } from 'quasar';
import AddAdministratorForm from '@/components/AdminCenter/AddAdministratorForm.vue';
import AddAdministratorDialog from './AddAdministratorDialog.vue';
import AdminRepository from '@/repositories/AdminRepository';
import type FlightSchool from '@/types/FlightSchool';
import type { NewFlightSchoolAdministrator, RequestableFlightSchoolAdmin } from '@/types/FlightSchoolAdmin';
import { useAdminStore } from '@/stores/admins';

const $q = useQuasar();

const props = defineProps<{
    school: FlightSchool,
}>();

const emit = defineEmits<{
    'update:go-back': [],
}>();

const adminStore = useAdminStore();

const adminsList = computed(() => adminStore.admins);
const saveLabel = computed(() => (adminsList.value.length > 1 ? 'Save All' : 'Save'));
const isAgreementChecked = ref(false);
const agreementText = 'All administrators have been provided a brief on the objectives and standards of the training courses per 14 CFR 141.79(d)(1)(i).';
const agreementInput = ref<QField>();

/**
 * Adds a new, empty administrator to the list of administrators which
 * controls the number of forms displayed.
 */
const addAdmin = () => {
    adminStore.addAdmin({
        email: '',
        emailSubscriber: false,
        valid: false,
    });
};

/**
 * Validates all of the forms and returns true if all of the forms are valid.
 */
const addAdminForm = ref<typeof AddAdministratorForm[]>();
const validate = async () => {
    if (!addAdminForm.value || !isAgreementChecked.value) {
        return false;
    }

    await Promise.all(addAdminForm.value.map((form) => form.validate()));
    return adminStore.admins.every((admin) => admin.valid);
};

/**
 * Adds the given admins to the school.
 */
const addCourseAdmins = async (admins: NewFlightSchoolAdministrator[]) => {
    const { courses } = props.school;

    // map admins to requestable admins
    const requestableAdmins: RequestableFlightSchoolAdmin[] = admins.map((admin) => ({
        email: admin.email,
        emailSubscriber: admin.emailSubscriber,
        courses,
    }));

    try {
        await AdminRepository.addCourseAdmins(props.school, requestableAdmins);

        $q.notify({
            type: 'positive',
            message: 'The administrators were successfully added.',
        });

        emit('update:go-back');
    } catch (error) {
        $q.notify({
            type: 'negative',
            message: 'There was an error adding the administrators. Please try again.',
        });
    }
};

/**
 * Attempts to save all of the admins to the school.
 * If the admins are valid, a dialog will be displayed to confirm the save.
 * If the admins are not valid, a notification will be displayed.
 */
const saveAllAdmins = async () => {
    const valid = await agreementInput.value?.validate() && await validate();

    if (valid) {
        const adminEmails = adminsList.value.map((admin) => admin.email);
        $q.dialog({
            component: AddAdministratorDialog,
            componentProps: {
                title: 'Confirm Save',
                message: 'Are you sure you want to add the following administrators to this school?',
                admins: adminEmails,
                persistent: true,
            },
        }).onOk(() => {
            addCourseAdmins(adminsList.value);
        });
    } else {
        $q.notify({
            type: 'negative',
            message: 'Please fix the errors on the form before saving.',
        });
    }
};

onBeforeUnmount(() => {
    adminStore.resetAdmins();
});
</script>

<template>
    <div class="q-pa-md">
        <div class="container">
            <div class="row justify-center">
                <div class="col col-md-8">
                    <h2 class="fsum-generic-header q-mb-xl text-center">
                        Add Administrator
                    </h2>
                    <div class="bg-white form-container">
                        <div class="column">
                            <AddAdministratorForm
                                v-for="(admin, index) in adminsList"
                                ref="addAdminForm"
                                :key="admin.id"
                                :school="school"
                                :admin="admin"
                                :index="index + 1"
                            />
                        </div>
                        <q-field
                            ref="agreementInput"
                            :model-value="isAgreementChecked"
                            class="agreement-wrapper"
                            :rules="[(val: boolean) => val || 'Please indicate that a brief has been provided.']"
                            borderless
                            dense
                        >
                            <template #control>
                                <q-checkbox
                                    v-model="isAgreementChecked"
                                    class="text-left agreement-checkbox"
                                    :label="agreementText"
                                    size="sm"
                                />
                            </template>
                        </q-field>
                        <div class="row justify-between q-pt-lg q-gutter-sm">
                            <q-btn
                                class="fsum-button-outlined outlined q-px-sm button-back"
                                label="Go Back"
                                icon="chevron_left"
                                no-caps
                                @click="$emit('update:go-back')"
                            />
                            <q-btn
                                class="fsum-button-dark-blue button-save"
                                :label="saveLabel"
                                no-caps
                                @click="saveAllAdmins"
                            />
                            <q-btn
                                class="fsum-button-dark-blue button-add"
                                label="Add Another Admin"
                                no-caps
                                @click="addAdmin"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
/** Makes the error message in line with the checkbox */
:deep(.agreement-wrapper.q-field--error .q-field__messages) {
    margin-left: 10px;
}

.form-container {
    padding: 36px;

    @media screen and (max-width: $breakpoint-sm-min) {
        padding: 20px;
    }
}
</style>
